import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PropTypes from 'prop-types';
import { TextEditorWidget } from '@redturtle/iovisit-theme/components';
import { UniversalLink } from '@plone/volto/components';
import { TextBlockView } from '@plone/volto-slate/blocks/Text';
import { useHandleDetachedBlockFocus } from '@redturtle/iovisit-theme/helpers/blocks.js';
import config from '@plone/volto/registry';
import cx from 'classnames';
import { flattenToAppURL } from '@plone/volto/helpers';

const messages = defineMessages({
  cta_title: {
    id: 'Type the title…',
    defaultMessage: 'Type the title…',
  },
  cta_content: {
    id: 'Type description…',
    defaultMessage: 'Digita la descrizione…',
  },
  openLinkInNewTab: {
    id: 'openLinkInNewTab',
    defaultMessage: 'Apri link in una nuova scheda',
  },
});

const Block = (props) => {
  const { data, block, inEditMode, selected, ...otherProps } = props;
  const {
    bgColor = 'primary',
    textAlignment = 'left',
    ctaColor = 'white',
    headlineTag = 'h2',
    size = 'm',
  } = data;
  const intl = useIntl();
  const Image = config.getComponent({ name: 'Image' }).component;
  const title = data?.cta_title;
  const hasImage = data?.showImage && data?.ctaImage?.length > 0;
  const content = data?.cta_content;
  const showFullwidth = data?.showFullWidth;

  const { selectedField, setSelectedField } = useHandleDetachedBlockFocus(
    props,
    'cta_title',
  );

  return (
    <div
      className={cx(`cta-block-wrapper bg-${bgColor} size-${size}`, {
        'has-image': hasImage,
        'full-width': showFullwidth,
      })}
    >
      {hasImage && data?.ctaImage?.length > 0 && (
        <figure className="img-wrapper">
          {!data.ctaImage?.[0]?.image_scales ? (
            <img
              src={flattenToAppURL(data.ctaImage?.[0].download)}
              alt={''}
              loading="lazy"
            />
          ) : (
            <Image
              item={data.ctaImage?.[0]}
              sizes="100vw"
              loading="lazy"
              responsive={true}
              alt=""
              aria-hidden="true"
              role="presentation"
              key={data.ctaImage?.[0]?.['@id']}
            />
          )}
        </figure>
      )}
      <div
        className={cx('content-wrapper', {
          'ui container': showFullwidth,
          'align-left': textAlignment === 'left',
          'align-center': textAlignment === 'center',
        })}
      >
        <div className={cx('cta-tile-text')}>
          {headlineTag === 'h2' ? (
            <h2 className="title mt-0" id={block + 'title'}>
              {inEditMode ? (
                <TextEditorWidget
                  {...otherProps}
                  showToolbar={false}
                  data={data}
                  fieldName="cta_title"
                  selected={selected && selectedField === 'cta_title'}
                  setSelected={setSelectedField}
                  block={block}
                  placeholder={intl.formatMessage(messages.cta_title)}
                  focusNextField={() => {
                    setSelectedField('cta_content');
                  }}
                />
              ) : typeof title === 'string' ? (
                title
              ) : (
                <TextBlockView data={{ value: title }} />
              )}
            </h2>
          ) : (
            <h3 className="title mt-0" id={block + 'title'}>
              {inEditMode ? (
                <TextEditorWidget
                  {...otherProps}
                  showToolbar={false}
                  data={data}
                  fieldName="cta_title"
                  selected={selected && selectedField === 'cta_title'}
                  setSelected={setSelectedField}
                  block={block}
                  placeholder={intl.formatMessage(messages.cta_title)}
                  focusNextField={() => {
                    setSelectedField('cta_content');
                  }}
                />
              ) : Array.isArray(title) ? (
                <TextBlockView data={{ value: title }} />
              ) : (
                title
              )}
            </h3>
          )}

          {inEditMode ? (
            <TextEditorWidget
              {...otherProps}
              showToolbar={true}
              data={data}
              fieldName="cta_content"
              block={block}
              selected={selected && selectedField === 'cta_content'}
              placeholder={intl.formatMessage(messages.cta_content)}
              setSelected={setSelectedField}
              focusPrevField={() => {
                setSelectedField('cta_title');
              }}
            />
          ) : Array.isArray(content) ? (
            <TextBlockView data={{ value: content }} />
          ) : (
            content
          )}
        </div>

        {data.ctaLink?.length > 0 && data.ctaLinkTitle?.length > 0 && (
          <div
            className={cx('cta-button-container', {
              'align-left': textAlignment === 'left',
              'align-center': textAlignment === 'center',
            })}
          >
            <UniversalLink
              className={`ui button ${ctaColor}`}
              disabled={inEditMode}
              href={data.ctaLink}
              openLinkInNewTab={!!data.openLinkInNewTab}
              title={
                !!data.openLinkInNewTab
                  ? intl.formatMessage(messages.openLinkInNewTab)
                  : null
              }
            >
              {data.ctaLinkTitle}
            </UniversalLink>
          </div>
        )}
      </div>
    </div>
  );
};

Block.propTypes = {
  entityMap: PropTypes.any,
  title: PropTypes.string,
  content: PropTypes.any,
};

export default Block;
